// @flow
import {
  REQUEST_APPROVALS,
  RECEIVE_APPROVALS,
  REQUEST_APPROVALS_COUNT,
  RECEIVE_APPROVALS_COUNT,
  REQUEST_UPDATE_APPROVAL_STATUS,
  RECEIVE_UPDATE_APPROVAL_STATUS,
  RECEIVE_UPDATE_APPROVAL_STATUS_ERROR,
  RECEIVE_APPROVALS_UNAUTHORIZED,
} from '../../ActionsConst';
import { APPROVAL_STATUS } from '../../../api/types/Approvals';
import type { ApprovalStatus, PendingApprovalCount, ApprovalList, Approval } from '../../../api/types/Approvals';
import ApprovalsServiceClient from '../../../clients/ApprovalsService/ApprovalsServiceClient';
import { showToast, createToastId, buildAndShowErrorToast } from '../../core/actions/toast/ToastActions';
import toastMsg from '../../../data/toast';

const requestApprovals = () => ({
  type: REQUEST_APPROVALS,
});

const requestApprovalsCount = () => ({
  type: REQUEST_APPROVALS_COUNT,
});

const requestUpdateApprovalStatus = (approval) => ({
  type: REQUEST_UPDATE_APPROVAL_STATUS,
  approval,
});

const receiveApprovals = (data: ApprovalList) => ({
  type: RECEIVE_APPROVALS,
  value: data,
});

const receiveApprovalsCount = (data: PendingApprovalCount) => ({
  type: RECEIVE_APPROVALS_COUNT,
  value: data,
});

const receiveUpdateApprovalStatus = (approval, status) => ({
  type: RECEIVE_UPDATE_APPROVAL_STATUS,
  approval,
  status,
});

const receiveUpdateApprovalStatusError = (approval) => ({
  type: RECEIVE_UPDATE_APPROVAL_STATUS_ERROR,
  approval,
});

const receiveApprovalsUnauthorized = () => ({
  type: RECEIVE_APPROVALS_UNAUTHORIZED,
});

const productApprovedSuccess = () =>
  showToast({
    id: createToastId(),
    message: {
      messageDescriptor: toastMsg.productApprovedSuccessMessage,
    },
  });

const productDeclinedSuccess = (requestedBy) =>
  showToast({
    id: createToastId(),
    message: {
      messageDescriptor: toastMsg.productDeclinedSuccessMessage,
      values: { employeeName: requestedBy },
    },
  });

const createScrollEvent = () => {
  if (typeof Event === 'function') {
    return new Event('scroll');
  }

  // IE11 fix as new Event(...) is not supported
  const event = document.createEvent('Event');
  event.initEvent('scroll', true, true);
  return event;
};

export const getApprovals = ({ status, fromItem }: { status: ApprovalStatus, fromItem: number }): Function => {
  return (dispatch: Function, getState: Function, baseApiUrl: string) => {
    dispatch(requestApprovals());
    return ApprovalsServiceClient.getApprovals({ baseApiUrl, status, fromItem })
      .then((approvals) => {
        dispatch(
          receiveApprovals({
            items: approvals.items,
            total: approvals.total,
          }),
        );
      })
      .catch((response) => {
        if (response && response.isUnauthorized) {
          dispatch(receiveApprovalsUnauthorized());
        } else {
          dispatch(
            receiveApprovals({
              items: [],
              total: 0,
            }),
          );
        }
      });
  };
};

export const getPendingApprovalsCount = (): Function => {
  return (dispatch: Function, getState: Function, apiUrl: string) => {
    dispatch(requestApprovalsCount());
    return ApprovalsServiceClient.getApprovalsCount(apiUrl, APPROVAL_STATUS.pending)
      .then((count) => {
        dispatch(
          receiveApprovalsCount({
            pendingCount: count,
          }),
        );
      })
      .catch(() => {
        dispatch(
          receiveApprovalsCount({
            pendingCount: 0,
          }),
        );
      });
  };
};

export const updateApprovalStatus = (approval: Approval, status: string, reason: string = ''): Function => {
  return (dispatch: Function, getState: Function, baseApiUrl: string) => {
    dispatch(requestUpdateApprovalStatus(approval));
    return ApprovalsServiceClient.updateApprovalStatus({
      baseApiUrl,
      status,
      packId: approval.id,
      projectId: approval.printProduct.projectId,
      reason,
    })
      .then(() => {
        dispatch(receiveUpdateApprovalStatus(approval, status));
        dispatch(getPendingApprovalsCount());

        if (status === APPROVAL_STATUS.approved) {
          dispatch(productApprovedSuccess());
        }

        if (status === APPROVAL_STATUS.declined) {
          dispatch(productDeclinedSuccess(approval.requestedBy));
        }

        // Trigger a scroll event so that the infinite scroll loads more if needed.
        window.dispatchEvent(createScrollEvent());
      })
      .catch(() => {
        dispatch(receiveUpdateApprovalStatusError(approval));
        dispatch(buildAndShowErrorToast(toastMsg.generalErrorReceivedMessage));
      });
  };
};

export default {
  getApprovals,
  getPendingApprovalsCount,
  updateApprovalStatus,
};
